import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import React, { createContext, useContext, useMemo, useState } from "react";
import { ReportData } from "./smart.actions.benchmark";
import { useLocalTranslation } from "../../../../modules/AiWidgets/nexthink/useLocalTranslation";

export type ActionFreeze = {
  healthCheck: boolean;
  triggerActions: boolean;
};

export type ActionStatusType = "success" | "failed" | "progress" | "partial" | undefined;

export type ActionStatus = {
  status: ActionStatusType;
  response?: any;
};

export type DeviceDetails = {
  id: string;
  name: string;
}

interface NexthinkContextType {
  healthCheckInfo: ReportData | null;
  setHealthCheckInfo: React.Dispatch<React.SetStateAction<ReportData | null>>;
  messageAndData: MessageWithActionAndData;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  actionStatus: ActionStatus;
  setActionStatus: React.Dispatch<React.SetStateAction<ActionStatus>>;
  actionFreeze: ActionFreeze;
  setActionFreeze: React.Dispatch<React.SetStateAction<ActionFreeze>>;
  manualRecommendedActions: string[];
  setManualRecommendedActions: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDevice: DeviceDetails;
  setSelectedDevice: React.Dispatch<React.SetStateAction<DeviceDetails>>;
  effectiveLanguage: string;
  localT: (key: string) => string;
}

const NexthinkContext = createContext<NexthinkContextType | undefined>(undefined);
NexthinkContext.displayName = "NexthinkContext";

export const NexthinkStateProvider = ({
  children,
  messageAndData,
}: {
  children: React.ReactNode;
  messageAndData: MessageWithActionAndData;
}) => {
  const queryLanguage = messageAndData?.data?.queryLanguage as string | undefined;
  const { localT, effectiveLanguage } = useLocalTranslation(queryLanguage);
  const [healthCheckInfo, setHealthCheckInfo] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState<ActionStatus>({ status: undefined });
  const [actionFreeze, setActionFreeze] = useState<ActionFreeze>({
    healthCheck: false,
    triggerActions: false,
  });
  const [manualRecommendedActions, setManualRecommendedActions] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceDetails>(() => {
    const list = messageAndData?.data?.deviceDetails?.data ?? [];
    return {
      id: list.length ? list[0]["device.collector.uid"] : null,
      name: list.length ? list[0]["device.name"] : null,
    };
  });

  const value = useMemo(
    () => ({
      healthCheckInfo,
      setHealthCheckInfo,
      messageAndData,
      loading,
      setLoading,
      actionStatus,
      setActionStatus,
      actionFreeze,
      setActionFreeze,
      manualRecommendedActions,
      setManualRecommendedActions,
      selectedDevice,
      setSelectedDevice,
      effectiveLanguage,
      localT,
    }),
    [healthCheckInfo, messageAndData, loading, actionStatus, actionFreeze, selectedDevice, effectiveLanguage, localT]
  );

  return <NexthinkContext.Provider value={value}>{children}</NexthinkContext.Provider>;
};

export const useNexthinkContext = () => {
  const context = useContext(NexthinkContext);
  if (!context) {
    throw new Error("useNexthinkContext must be used within NexthinkStateProvider");
  }
  return context;
};
